import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faCircleHalfStroke,
  faCircleCheck,
  faMinus,
  faSlash,
} from '@fortawesome/free-solid-svg-icons';
import type { StepStatus } from '../../services/procedure-execution.service';

interface Option {
  value: StepStatus;
  label: string;
  icon: typeof faCircle;
  activeClass: string;
}

const OPTIONS: Option[] = [
  {
    value: 'IN_PROGRESS',
    label: 'In Progress',
    icon: faCircleHalfStroke,
    activeClass: 'border-amber-500 bg-amber-500/10 text-amber-700',
  },
  {
    value: 'COMPLETED',
    label: 'Completed',
    icon: faCircleCheck,
    activeClass: 'border-green-600 bg-green-600/10 text-green-700',
  },
  {
    value: 'SKIPPED',
    label: 'Skipped',
    icon: faMinus,
    activeClass: 'border-slate-500 bg-slate-500/10 text-slate-600',
  },
  {
    value: 'NOT_APPLICABLE',
    label: 'N/A',
    icon: faSlash,
    activeClass: 'border-slate-400 bg-slate-400/10 text-slate-500',
  },
];

interface Props {
  value: StepStatus;
  onChange: (status: StepStatus) => void;
  disabled?: boolean;
}

export function StepStatusSelector({ value, onChange, disabled = false }: Props) {
  return (
    <>
      {/* Desktop: segmented control */}
      <div className="hidden sm:flex rounded-lg border border-border overflow-hidden">
        {OPTIONS.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              disabled={disabled}
              className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition border-r last:border-r-0 border-border
                ${active ? opt.activeClass : 'text-muted-foreground hover:bg-muted'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-pressed={active}
            >
              <FontAwesomeIcon icon={opt.icon} className="h-3 w-3" />
              <span className="hidden md:inline">{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Mobile: radio cards */}
      <div className="grid grid-cols-2 gap-2 sm:hidden">
        {OPTIONS.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              disabled={disabled}
              className={`flex items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition min-h-[48px]
                ${active ? opt.activeClass : 'border-border text-muted-foreground hover:bg-muted'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-pressed={active}
            >
              <FontAwesomeIcon icon={opt.icon} className="h-4 w-4 shrink-0" />
              {opt.label}
            </button>
          );
        })}
      </div>
    </>
  );
}
